// src/lib/errors.ts
export type AppErrorCode =
  | "NETWORK"
  | "TIMEOUT"
  | "MAINTENANCE"
  | "BAD_REQUEST"
  | "UNAUTHORIZED"
  | "FORBIDDEN"
  | "NOT_FOUND"
  | "CONFLICT"
  | "SERVER"
  | "INVALID_RESPONSE"
  | "CONFIG"
  | "SESSION_EXPIRED"
  | "UNKNOWN";

export type AppErrorMeta = {
  endpoint?: string;
  fallbackFromEndpoint?: string;
  fallbackAttempts?: Array<{
    endpoint: string;
    status?: number;
    contentType?: string;
    result: "success" | "non_json" | "http_error" | "network_error";
  }>;
  method?: string;
  status?: number;
  responseText?: string;
  responseUrl?: string;
  responseContentType?: string;
  location?: string;
  redirected?: boolean;
  causeName?: string;
};

export class AppError extends Error {
  code: AppErrorCode;
  meta: AppErrorMeta;

  constructor(message: string, code: AppErrorCode = "UNKNOWN", meta: AppErrorMeta = {}) {
    super(message);
    this.name = "AppError";
    this.code = code;
    this.meta = meta;
  }
}

export function getBackendErrorCode(errorValue: unknown): string | undefined {
  if (!(errorValue instanceof AppError)) return undefined;

  try {
    const parsed = JSON.parse(errorValue.meta?.responseText ?? "") as Record<string, unknown>;
    return (
      (parsed.errorCode as string | undefined) ??
      ((parsed.data as Record<string, unknown> | undefined)?.errorCode as string | undefined)
    );
  } catch {
    return undefined;
  }
}

export function toUserMessage(e: unknown): string {
  if (e instanceof AppError) {
    switch (e.code) {
      case "CONFIG":
        return "設定が不足しています。環境変数を確認してください。";
      case "NETWORK":
        return "通信に失敗しました。ネットワーク状況を確認してください。";
      case "TIMEOUT":
        return "通信がタイムアウトしました。時間をおいて再度お試しください。";
      case "MAINTENANCE":
        return "現在メンテナンス中です。しばらくしてからもう一度お試しください。";
      case "UNAUTHORIZED":
        return "認証に失敗しました。再度お試しください。";
      case "FORBIDDEN": {
        const errorCode = getBackendErrorCode(e);
        if (errorCode === "group_talk_session_safety_limit") {
          return "この壁打ちは長くなってきました。新しい壁打ちで続きを始めてください。";
        }
        if (errorCode === "limit_exceeded") return "月間の利用上限に達しました。プランページで上限を確認できます。";
        if (errorCode === "feature_not_available") return "ご利用のプランでは対応していない機能です。";
        return "権限がありません。";
      }
      case "NOT_FOUND":
        return "対象が見つかりません。設定をご確認ください。";
      case "BAD_REQUEST":
        return "リクエスト内容に誤りがあります。";
      case "CONFLICT":
        return "競合が発生しました。時間をおいて再度お試しください。";
      case "SERVER":
        return "サーバーエラーが発生しました。時間をおいて再度お試しください。";
      case "INVALID_RESPONSE":
        return "サーバー応答の形式が不正です。";
      case "SESSION_EXPIRED":
        return "セッションが切れました。再度サインインしてください。";
      default:
        return "エラーが発生しました。再度お試しください。";
    }
  }

  return "予期しないエラーが発生しました。";
}

function safeGetUserId(): string | undefined {
  try {
    // Lazy import to avoid circular dependency at module load time
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    const { storage } = require("./storage") as typeof import("./storage");
    return storage.getAuth().userId;
  } catch {
    return undefined;
  }
}

export function logError(error: unknown, context?: Record<string, unknown>): void {
  const userId = safeGetUserId();
  const enriched: Record<string, unknown> = { userId, ...context };

  const { sentryCaptureException } = require("./sentry") as typeof import("./sentry");
  sentryCaptureException(error, enriched);

  if (error instanceof AppError) {
    console.error("[AppError]", {
      ...enriched,
      code: error.code,
      message: error.message,
      meta: error.meta,
    });
    return;
  }

  if (error instanceof Error) {
    console.error("[Error]", {
      ...enriched,
      name: error.name,
      message: error.message,
    });
    return;
  }

  console.error("[UnknownError]", enriched, error);
}
